/**
 * SessionContext — estado global de la sesión del usuario.
 *
 * Expone:
 *  - token       : bearer vigente (ID token de Cognito; marcador en mock)
 *  - descriptor  : proyección segura completa (R-PT-02, R-PT-03)
 *  - user        : descriptor.user
 *  - tenant      : descriptor.tenant
 *  - capabilities: descriptor.capabilities
 *  - chats       : descriptor.chats (lista real del bootstrap; R-PT-06)
 *  - login()     → mock: bootstrap directo; real: Cognito PKCE (hosted UI)
 *                  y después POST /v1/session/bootstrap con Bearer
 *  - logout()
 *
 * R-PT-05: la identidad se delega — la SPA nunca ve la contraseña.
 * R-PT-09: sin estado autoritativo en el cliente. US-62: la lista de chats se
 *   sirve SOLO del descriptor; Pórtico no persiste ninguna caché de chats.
 * R-PT-10: no se envía ningún dato de tenant a terceros.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { bootstrap, isMock, setSessionExpiredHandler } from '../api/client.js'
import { startSessionTimers } from '../lib/session-policy.js'
import { checkContractCompatibility, contractWarningMessage } from '../lib/contract.js'
import { logWarn } from '../lib/log.js'
import { resolveAgentPalette } from '../lib/agent-colors.js'
import * as auth from '../auth/cognito.js'

const SessionContext = createContext(null)

// ---------------------------------------------------------------------------
// Tema claro/oscuro — Gabinete (#280, SPEC §A.7). Cosmético de cliente, no
// toca contrato ni descriptor (R-PT-08/US-59: clave whitelisted, nunca
// contenido/token). Default "Sistema" (sigue prefers-color-scheme); solo al
// elegir Claro/Oscuro se fija data-theme y se persiste.
// ---------------------------------------------------------------------------
const THEME_STORAGE_KEY = 'portico:theme'

function systemPrefersDark() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches
}

function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

function resolveTheme(preference) {
  return preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference
}

// ---------------------------------------------------------------------------
// US-62: Pórtico no almacena memoria. La lista de chats se sirve ÍNTEGRAMENTE
// del descriptor de bootstrap (`descriptor.chats`, poblado server-side desde el
// SESSION_STORE vía GSI user-chats — #244, verificado E2E). Ya no hay caché
// local de metadatos de chats: se retira la deuda provisional del enlace AG-08
// (PR #20), que existía solo mientras el descriptor no listaba chats. El
// historial de cada chat se sigue pidiendo bajo demanda por GET (R-PT-06), y el
// cliente no guarda estado autoritativo de conversaciones (R-PT-09 / US-65).
// ---------------------------------------------------------------------------

function mensajeError(e) {
  const m = String(e?.message || e)
  if (m.includes('401')) return 'La sesión no es válida o ha caducado. Vuelve a entrar.'
  if (m.includes('403')) return 'Tu usuario no está autorizado en este tenant.'
  return `No se pudo arrancar la sesión: ${m}`
}

export function SessionProvider({ children }) {
  const [token, setToken]           = useState(null)
  const [descriptor, setDescriptor] = useState(null)
  const [loading, setLoading]       = useState(!isMock) // real: resolviendo el redirect
  const [error, setError]           = useState(null)
  // US-68: veredicto de compatibilidad de contrato del último bootstrap.
  const [contract, setContract]     = useState(null)
  // Parada de los temporizadores de sesión (US-69). Se guarda en ref para no
  // reprovocar renders y poder pararlos desde cualquier cierre de sesión.
  const stopTimersRef = useRef(null)
  // US-467/US-69: ancla del tope absoluto de sesión. Se fija con el `issued_at`
  // del PRIMER bootstrap y se conserva en los refrescos, para que un
  // re-bootstrap (p.ej. refrescar la lista de chats tras borrar uno) NO
  // reinicie la vida máxima de la sesión, aunque el kernel acuñe un `issued_at`
  // nuevo en cada bootstrap (defensa cliente-side; ver F070 y nota H1/H3).
  const issuedAtAnchorRef = useRef(null)

  // Tema — independiente de la sesión (aplica también en Login). "Sistema"
  // por defecto; Claro/Oscuro fija y persiste (SPEC §A.7).
  const [themePref, setThemePref] = useState(readStoredTheme)
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(readStoredTheme()))

  const setTheme = useCallback((pref) => {
    setThemePref(pref)
    try {
      if (pref === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
      else localStorage.setItem(THEME_STORAGE_KEY, pref)
    } catch { /* almacenamiento no disponible (privado/bloqueado): no persiste, sigue en memoria */ }
  }, [])

  useEffect(() => {
    setResolvedTheme(resolveTheme(themePref))
    if (themePref !== 'system' || typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setResolvedTheme(resolveTheme('system'))
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [themePref])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme)
  }, [resolvedTheme])

  // Cierre de sesión local: descarta los tokens en memoria y el estado de UI y
  // para los temporizadores. Punto único que comparten logout, 401, idle y vida
  // máxima (US-69); `message` es null para un logout voluntario y un texto para
  // las expiraciones, que lo pintan en el Login.
  const endSession = useCallback((message = null) => {
    stopTimersRef.current?.()
    stopTimersRef.current = null
    // El próximo login vuelve a anclar el tope absoluto desde cero.
    issuedAtAnchorRef.current = null
    auth.clearTokens()
    setToken(null)
    setDescriptor(null)
    setContract(null)
    // Limpiar design tokens del documento
    document.documentElement.style.removeProperty('--color-primary')
    document.documentElement.style.removeProperty('--font-body')
    setError(message)
  }, [])

  // Aplica un descriptor recién obtenido (login o refresh): estado de sesión,
  // compatibilidad de contrato, design tokens del tenant y (re)arranque de los
  // temporizadores de sesión. Punto único que comparten el login y el refresh.
  const applyDescriptor = useCallback((t, d) => {
    setToken(t)
    setDescriptor(d)
    // US-68 (#202): comparar la versión de contrato del kernel con la que
    // declara el front. Si no coincide el MAJOR, se degrada con aviso (banner
    // en AppShell) en vez de asumir compatibilidad y romper de forma silenciosa.
    // Solo se registra la versión (metadato técnico), nunca contenido del
    // tenant — R-PT-10 (soberanía de telemetría, US-66).
    const compat = checkContractCompatibility(d?.contract_version)
    setContract(compat)
    if (compat.status !== 'ok') {
      logWarn('contract_version_mismatch', {
        status: compat.status,
        declared: compat.declared,
        received: compat.received,
      })
    }
    // Inyectar design tokens del tenant en el documento (R-PT-02)
    if (d?.tenant?.branding) {
      const { color_primario, tipografia } = d.tenant.branding
      if (color_primario)
        document.documentElement.style.setProperty('--color-primary', color_primario)
      if (tipografia)
        document.documentElement.style.setProperty('--font-body', tipografia)
    }
    // US-69: idle + vida máxima según SESSION_POLICY del tenant. El tope
    // absoluto se ancla al `issued_at` del PRIMER bootstrap (issuedAtAnchorRef)
    // y se conserva en los refrescos, así un re-bootstrap no reinicia la vida
    // máxima. Los timers se rearrancan en cada bootstrap para no acumular.
    if (!issuedAtAnchorRef.current) issuedAtAnchorRef.current = d?.session?.issued_at ?? null
    const issuedAt = issuedAtAnchorRef.current ?? d?.session?.issued_at
    stopTimersRef.current?.()
    stopTimersRef.current = startSessionTimers({
      policy: d?.SESSION_POLICY,
      issuedAt,
      onIdle: () => endSession('Sesión cerrada por inactividad. Vuelve a entrar.'),
      onAbsolute: () => endSession('Tu sesión alcanzó su duración máxima. Vuelve a entrar.'),
    })
  }, [endSession])

  // Bootstrap contra el kernel (o el mock) y arranque de la sesión de UI.
  const openSession = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { token: t, descriptor: d } = await bootstrap()
      applyDescriptor(t, d)
    } catch (err) {
      setError(mensajeError(err))
    } finally {
      setLoading(false)
    }
  }, [applyDescriptor])

  // Refresco silencioso desde el store (US-467 / US-62 / R-PT-09): re-bootstrap
  // para traer `descriptor.chats` actualizado tras una mutación gobernada (p.ej.
  // borrar un chat), reflejando la VERDAD del store, no una edición local. El
  // contrato v1.0 no tiene "listar chats" — la lista solo llega en el descriptor
  // — así que refrescar es re-bootstrap (el mecanismo `silent-rebootstrap` del
  // §4). No toca `loading` (no desmonta el shell) ni el ancla de vida máxima. Si
  // falla, conserva la sesión y propaga el error para que lo trate el llamante.
  const refresh = useCallback(async () => {
    const { token: t, descriptor: d } = await bootstrap()
    applyDescriptor(t, d)
  }, [applyDescriptor])

  // Entrar: en mock va directo; en real, sin token -> hosted UI de Cognito
  // (code + PKCE; el primer login fuerza el cambio de contraseña temporal, #243).
  const login = useCallback(async () => {
    setError(null)
    if (!isMock && !auth.getBearerToken()) return auth.login()
    await openSession()
  }, [openSession])

  const logout = useCallback(() => { endSession(null) }, [endSession])

  // Al montar (solo modo real): si venimos del redirect de Cognito, canjear
  // el code y entrar; si no, quedarse en el Login. Y registrar el manejo
  // central del 401: tirar sesión -> pantalla de Login -> re-login.
  useEffect(() => {
    // 401 del kernel (contrato §4): tirar sesión -> Login -> re-login.
    setSessionExpiredHandler(() => endSession('La sesión ha caducado. Vuelve a entrar.'))
    if (isMock) return
    auth.completeLoginFromUrl()
      .then(venia => (venia ? openSession() : setLoading(false)))
      .catch(err => { setError(mensajeError(err)); setLoading(false) })
  }, [openSession, endSession])

  // Al desmontar el provider: parar cualquier temporizador vivo (US-69).
  useEffect(() => () => stopTimersRef.current?.(), [])

  const agents = descriptor?.agents_visibles ?? []
  // Paleta de agentes (#280) — determinista, memoizada sobre la lista de
  // agentes visibles del descriptor (ADR-280 §4 / SPEC §C.1).
  const agentPalette = useMemo(() => resolveAgentPalette(agents), [agents])

  const value = {
    token,
    descriptor,
    user: descriptor?.user ?? null,
    tenant: descriptor?.tenant ?? null,
    capabilities: descriptor?.capabilities ?? null,
    agents,
    agentPalette,
    chats: descriptor?.chats ?? [],
    loading,
    error,
    login,
    logout,
    // US-467: refresco de la sesión desde el store (re-bootstrap silencioso),
    // usado tras una acción gobernada que muta el store (p.ej. borrar un chat).
    refresh,
    isAuthenticated: !!descriptor,
    // US-68: estado de compatibilidad de contrato y aviso legible (null si 'ok').
    contract,
    contractWarning: contractWarningMessage(contract),
    // Tema (#280 SPEC §A.7): preferencia del usuario ('system'|'light'|'dark')
    // y el tema efectivamente aplicado ('light'|'dark').
    theme: themePref,
    resolvedTheme,
    setTheme,
  }

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession debe usarse dentro de <SessionProvider>')
  return ctx
}
